import { MaybeDrafted } from '@reduxjs/toolkit/dist/query/core/buildThunks';
import type {
  ApiResponse,
  CreateConversationFromEmailApiArg,
  CreateConversationFromEmailApiResponse,
  CreateConversationFromSmsApiArg,
  CreateConversationFromSmsApiResponse,
  CreateConversationMessageApiArg,
  CreateConversationMessageApiResponse,
  CreateConversationMessageBulkApiArg,
  CreateConversationsCsvApiArg,
  CreateConversationWrapupMessageApiArg,
  CreateConversationWrapupMessageApiResponse,
  CreateConversationWrapupMessageBulkApiArg,
  CreateScheduledConversationBatchApiArg,
  CreateScheduledConversationBatchApiResponse,
  ReadConversationActivityLogApiArg,
  ReadConversationActivityLogApiResponse,
  ReadConversationApiArg,
  ReadConversationApiResponse,
  ReadConversationAutomationActivitiesApiArg,
  ReadConversationAutomationActivitiesApiResponse,
  ReadConversationBulkActionApiArg,
  ReadConversationBulkActionApiResponse,
  ReadConversationBulkActionFailedActionsApiArg,
  ReadConversationBulkActionFailedActionsApiResponse,
  ReadConversationCustomCardApiArg,
  ReadConversationCustomCardApiResponse,
  ReadConversationFlowActivitiesApiArg,
  ReadConversationFlowActivitiesApiResponse,
  ReadConversationMessageDeliveryEventsArg,
  ReadConversationMessageDeliveryEventsResponse,
  ReadConversationMessagesApiArg,
  ReadConversationMessagesApiResponse,
  ReadConversationsValidationBulkApiArg,
  ReadConversationsValidationBulkApiResponse,
  ReadOrGenerateSummaryApiArg,
  ReadOrGenerateSummaryApiResponse,
  ReadRequesterConversationsApiArg,
  ReadRequesterConversationsApiResponse,
  ReadScheduledConversationBatchesApiResponse,
  RedactConversationMessageApiArg,
  ResultApiResponse,
  SuggestConversationTagsApiArg,
  SuggestConversationTagsApiResponse,
  Tag,
  UpdateConversationAssignmentAnswerApiArg,
  UpdateConversationAssignmentAnswerApiResponse,
  UpdateConversationAssignmentTakeoverApiArg,
  UpdateConversationAssignmentTakeoverApiResponse,
  UpdateConversationAssistedAssignmentTransferAnswerApiArg,
  UpdateConversationAssistedAssignmentTransferAnswerApiResponse,
  UpdateConversationAssistedAssignmentTransferApiArg,
  UpdateConversationAssistedAssignmentTransferApiResponse,
  UpdateConversationBlindAssignmentTransferAnswerApiArg,
  UpdateConversationBlindAssignmentTransferAnswerApiResponse,
  UpdateConversationBlindAssignmentTransferApiArg,
  UpdateConversationBlindAssignmentTransferApiResponse,
  UpdateConversationCallerApiArg,
  UpdateConversationCallRecordingPlaybackApiArg,
  UpdateConversationCallRecordingPlaybackApiResponse,
  UpdateConversationCancelCsatApiArg,
  UpdateConversationCancelPendingApiArg,
  UpdateConversationCancelPendingApiResponse,
  UpdateConversationCloseApiArg,
  UpdateConversationCloseApiResponse,
  UpdateConversationCloseBulkApiArg,
  UpdateConversationHoldCallApiArg,
  UpdateConversationLanguageApiArg,
  UpdateConversationLanguageApiResponse,
  UpdateConversationLinkApiArg,
  UpdateConversationLinkApiResponse,
  UpdateConversationLinkBulkApiArg,
  UpdateConversationMuteCallApiArg,
  UpdateConversationPendingStatusApiArg,
  UpdateConversationPendingStatusApiResponse,
  UpdateConversationQueueTransferApiArg,
  UpdateConversationQueueTransferApiResponse,
  UpdateConversationQueueTransferBulkApiArg,
  UpdateConversationTagApiArg,
  UpdateConversationTagApiResponse,
  UpdateConversationTagBulkApiArg,
  UpdateConversationToggleResolveApiArg,
  UpdateConversationUnlinkApiArg,
  UpdateConversationWorkCompleteTimeApiArg,
  UpdateConversationWorkTimeCompleteApiResponse,
  UpdateConversationWorkTimeExtendApiArg,
  UpdateConversationWorkTimeExtendApiResponse,
} from 'lib-shared-types-api';
import { DeepWritable } from 'ts-essentials';

import { api } from 'app/app.api';
import { actions as conversationActions } from 'components/conversation/actions/conversation';
import { ConversationTaggingEventPayload, pubSub } from 'utils/pubSub';

import {
  transformReadConversationBulkAction,
  transformReadConversationBulkActionFailedActions,
  transformReadConversationMessageDeliveryEvents,
  transformReadConversationMessagesResponse,
  transformReadConversationResponse,
  transformReadScheduledConversationBatchesResponse,
} from './utils/transformResponse';

export type UpdateConversationSubjectApiArg = {
  csid: number;
  subject: string;
};

export type UpdateConversationSubjectApiResponse = ApiResponse<{
  subject: string;
}>;

export const conversationsApi = api.injectEndpoints({
  endpoints: (builder) => ({
    readRequesterConversations: builder.query<
      ReadRequesterConversationsApiResponse,
      ReadRequesterConversationsApiArg
    >({
      query: ({ userId }) => ({
        version: 2,
        path: `conversations/requester/${userId}`,
        params: {
          limit: 500,
          attributes: [
            'id',
            'status',
            'created_at',
            'initial_channel',
            'direction',
            'attributes',
          ].join(','),
        },
      }),
    }),
    readConversation: builder.query<
      ReadConversationApiResponse,
      ReadConversationApiArg
    >({
      query: ({ csid }) => ({
        version: 2,
        path: `conversations/${csid}`,
      }),
      transformResponse: transformReadConversationResponse,
      onCacheEntryAdded: async (
        arg,
        { cacheDataLoaded, cacheEntryRemoved, dispatch },
      ) => {
        const { csid } = arg;
        await cacheDataLoaded;

        const handleConversationTagged = (
          payload: ConversationTaggingEventPayload,
        ) => {
          if (payload.csid !== csid) return;

          dispatch(
            conversationsApi.util.updateQueryData(
              'readConversation',
              { csid },
              (draft) => {
                const mutableDraft = draft as MaybeDrafted<
                  DeepWritable<typeof draft>
                >;

                if (mutableDraft?.data?.history?.main_conversation) {
                  const tags = draft.data.history.main_conversation.tags || [];

                  const existingTagIdx = tags.findIndex(
                    (tag) => tag.name === payload.tag.name,
                  );
                  // Only add if not already present
                  if (existingTagIdx === -1) {
                    mutableDraft.data.history.main_conversation.tags.push({
                      id: payload.tag.id,
                      name: payload.tag.name,
                      color: payload.tag.color,
                      type: 'conversation',
                      usage_count: 1,
                      is_deactivated: false,
                    });
                  } else {
                    const existingTag =
                      mutableDraft.data.history.main_conversation.tags[
                        existingTagIdx
                      ];

                    existingTag.id = payload.tag.id;
                    existingTag.color = payload.tag.color;
                  }
                }
              },
            ),
          );
        };

        const handleConversationUntagged = (
          payload: ConversationTaggingEventPayload,
        ) => {
          if (payload.csid !== csid) return;

          dispatch(
            conversationsApi.util.updateQueryData(
              'readConversation',
              { csid },
              (draft) => {
                const mutableDraft = draft as MaybeDrafted<
                  DeepWritable<typeof draft>
                >;

                if (mutableDraft?.data?.history?.main_conversation) {
                  mutableDraft.data.history.main_conversation.tags =
                    mutableDraft.data.history.main_conversation.tags.filter(
                      (tag) => tag.id !== payload.tag.id,
                    );
                }
              },
            ),
          );
        };

        // Subscribe to tag updates
        const unsubscribeTagged = pubSub.subscribe(
          'conversationTagged',
          handleConversationTagged,
        );
        const unsubscribeUntagged = pubSub.subscribe(
          'conversationUntagged',
          handleConversationUntagged,
        );

        // When the cache entry is removed, clean up the subscription
        await cacheEntryRemoved;
        unsubscribeTagged();
        unsubscribeUntagged();
      },
    }),
    updateConversationLink: builder.mutation<
      UpdateConversationLinkApiResponse,
      UpdateConversationLinkApiArg
    >({
      query: ({ link_type, source_csid, target_csid }) => ({
        version: 2,
        path: `conversations/${source_csid}/link/${target_csid}`,
        method: 'PUT',
        body: { link_type },
      }),
    }),
    updateConversationUnlink: builder.mutation<
      ResultApiResponse,
      UpdateConversationUnlinkApiArg
    >({
      query: ({ csid }) => ({
        version: 1,
        path: `conversations/${csid}/unlink`,
        method: 'PUT',
      }),
    }),
    updateConversationResolve: builder.mutation<
      // Response is 204.
      void,
      UpdateConversationToggleResolveApiArg
    >({
      query: ({ csid, resolve, author_id }) => ({
        version: 4,
        path: `conversations/${csid}/resolve?author_id=${author_id}`,
        method: resolve ? 'PUT' : 'DELETE',
      }),
      async onQueryStarted({ csid, resolve }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            conversationActions.conversationResolutionStatusChange({
              csid,
              isResolved: resolve,
              resolvedAt: new Date().toISOString(),
              resolvedBy: '',
            }),
          );
        } catch {
          // Do nothing.
        }
      },
    }),
    updateConversationAssignmentTakeover: builder.mutation<
      UpdateConversationAssignmentTakeoverApiResponse,
      UpdateConversationAssignmentTakeoverApiArg
    >({
      query: ({ csid }) => ({
        version: 1,
        path: `conversations/${csid}/claim`,
        method: 'PUT',
      }),
    }),
    updateConversationAssignmentAnswer: builder.mutation<
      UpdateConversationAssignmentAnswerApiResponse,
      UpdateConversationAssignmentAnswerApiArg
    >({
      query: ({ csid, didAccept: did_accept }) => ({
        version: 1,
        path: `conversations/${csid}/respond`,
        method: 'PUT',
        body: { did_accept },
      }),
    }),
    updateConversationClose: builder.mutation<
      UpdateConversationCloseApiResponse,
      UpdateConversationCloseApiArg
    >({
      query: ({ csid }) => ({
        version: 1,
        path: `conversations/${csid}/close`,
        method: 'PUT',
      }),
    }),
    updateConversationWorkTimeComplete: builder.mutation<
      UpdateConversationWorkTimeCompleteApiResponse,
      UpdateConversationWorkCompleteTimeApiArg
    >({
      query: ({ csid }) => ({
        version: 1,
        path: `conversations/${csid}/acw`,
        method: 'DELETE',
      }),
    }),
    updateConversationWorkTimeExtend: builder.mutation<
      UpdateConversationWorkTimeExtendApiResponse,
      UpdateConversationWorkTimeExtendApiArg
    >({
      query: ({ csid, duration: duration_in_seconds }) => ({
        version: 1,
        path: `conversations/${csid}/acw`,
        method: 'PUT',
        body: { duration_in_seconds },
      }),
    }),
    readConversationActivityLog: builder.query<
      ReadConversationActivityLogApiResponse,
      ReadConversationActivityLogApiArg
    >({
      query: ({ csid }) => ({
        version: 1,
        path: `conversations/${csid}/activity_log`,
      }),
    }),
    readConversationFlowActivities: builder.query<
      ReadConversationFlowActivitiesApiResponse,
      ReadConversationFlowActivitiesApiArg
    >({
      query: ({ csid }) => ({
        version: 1,
        path: `conversations/${csid}/flow_activities`,
      }),
    }),
    updateConversationLanguage: builder.mutation<
      UpdateConversationLanguageApiResponse,
      UpdateConversationLanguageApiArg
    >({
      query: ({ csid, language }) => ({
        version: 1,
        path: `conversations/${csid}/language`,
        method: 'POST',
        body: { language },
      }),
    }),
    updateConversationTag: builder.mutation<
      UpdateConversationTagApiResponse,
      UpdateConversationTagApiArg
    >({
      query: ({ action, csid, tag }) => ({
        version: 1,
        ...(action === 'add'
          ? {
              path: `conversations/${csid}/tags`,
              method: 'POST',
              body: { name: tag.name },
            }
          : {
              path: `conversations/${csid}/tags/${tag.name}`,
              method: 'DELETE',
            }),
      }),
      async onQueryStarted(
        { action, csid, tag },
        { dispatch, queryFulfilled },
      ) {
        const patchResult = dispatch(
          conversationsApi.util.updateQueryData(
            'readConversation',
            { csid },
            (draft) => {
              const tags = draft.data.history.main_conversation.tags as Tag[];

              if (action === 'add') {
                tags.unshift(tag);
              } else {
                tags.splice(
                  tags.findIndex(({ name }) => name === tag.name),
                  1,
                );
              }
            },
          ),
        );

        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),
    updateConversationSubject: builder.mutation<
      UpdateConversationSubjectApiResponse,
      UpdateConversationSubjectApiArg
    >({
      query: ({ csid, subject }) => ({
        version: 1,
        path: `conversations/${csid}/subject`,
        method: 'PUT',
        body: { subject },
      }),
      async onQueryStarted({ csid, subject }, { dispatch, queryFulfilled }) {
        let previousSubject: string | undefined;

        const patchResult = dispatch(
          conversationsApi.util.updateQueryData(
            'readConversation',
            { csid },
            (draft) => {
              const mutableDraft = draft as MaybeDrafted<
                DeepWritable<typeof draft>
              >;

              if (mutableDraft?.data?.history?.main_conversation) {
                if (!mutableDraft.data.history.main_conversation.attributes) {
                  mutableDraft.data.history.main_conversation.attributes = {};
                }
                const attributes = mutableDraft.data.history.main_conversation
                  .attributes as DeepWritable<
                  typeof mutableDraft.data.history.main_conversation.attributes
                > & { subject?: string };

                previousSubject = attributes.subject;
                attributes.subject = subject;
              }
            },
          ),
        );

        dispatch(
          conversationActions.conversationSubjectUpdate({ csid, subject }),
        );

        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
          if (previousSubject !== undefined) {
            dispatch(
              conversationActions.conversationSubjectUpdate({
                csid,
                subject: previousSubject,
              }),
            );
          }
        }
      },
    }),
    createConversationFromEmail: builder.mutation<
      CreateConversationFromEmailApiResponse,
      CreateConversationFromEmailApiArg
    >({
      query: ({
        from: from_email,
        to: to_email,
        cc: cc_emails,
        bcc: bcc_emails,
        attachments: attachments_v2,
        contentType: content_type,
        wrapup: internal_note,
        queueId: queue_id,
        shouldOffer: offer,
        parentConversationLink,
        tags,
        subject,
        body,
        language,
      }) => ({
        version: 1,
        path: 'conversations/email',
        method: 'POST',
        body: {
          from_email,
          to_email,
          subject,
          body,
          content_type,
          ...(cc_emails?.length && { cc_emails }),
          ...(bcc_emails?.length && { bcc_emails }),
          ...(attachments_v2?.length && { attachments_v2 }),
          ...(tags?.length && { tags: tags.map(({ id }) => id) }),
          ...(typeof language !== 'undefined' && { language }),
          ...(typeof queue_id !== 'undefined' && { queue_id }),
          ...(typeof offer !== 'undefined' && { offer }),
          ...(typeof internal_note !== 'undefined' && { internal_note }),
          ...(typeof parentConversationLink !== 'undefined' && {
            link: {
              parent_csid: parentConversationLink.csid,
              ...(typeof parentConversationLink.type !== 'undefined' && {
                link_type: parentConversationLink.type,
              }),
            },
          }),
        },
      }),
    }),

    createConversationFromSms: builder.mutation<
      CreateConversationFromSmsApiResponse,
      CreateConversationFromSmsApiArg
    >({
      query: ({
        from: from_phone_number,
        to: to_phone_number,
        queueId: queue_id,
        tags,
        message: body,
      }) => ({
        version: 1,
        path: 'conversations/sms',
        method: 'POST',
        body: {
          from_phone_number,
          to_phone_number,
          body,
          ...(tags?.length && { tags: tags.map(({ id }) => id) }),
          queue_id,
        },
      }),
    }),
    readConversationMessages: builder.query<
      ReadConversationMessagesApiResponse,
      ReadConversationMessagesApiArg
    >({
      query: ({ csid }) => ({
        version: 1,
        path: `conversations/${csid}/messages`,
      }),
      transformResponse: transformReadConversationMessagesResponse,
    }),
    createConversationMessage: builder.mutation<
      CreateConversationMessageApiResponse,
      CreateConversationMessageApiArg
    >({
      query: ({
        csid,
        text,
        channel,
        from,
        to: to_emails,
        cc: cc_emails,
        bcc: bcc_emails,
        contentType: content_type,
        attachments: attachedfiles_v2,
      }) => ({
        version: 1,
        path: `conversations/${csid}/messages`,
        method: 'POST',
        body: {
          channel,
          ...(typeof to_emails !== 'undefined' && { to_emails }),
          ...(typeof cc_emails !== 'undefined' && { cc_emails }),
          ...(typeof bcc_emails !== 'undefined' && { bcc_emails }),
          attributes: {
            ...(typeof from !== 'undefined' && { from }),
            ...(typeof attachedfiles_v2 !== 'undefined' && {
              attachedfiles_v2,
            }),
            ...(typeof content_type !== 'undefined' &&
              content_type === 'html' && { content_type }),
            text,
          },
        },
      }),
    }),
    createConversationWrapupMessage: builder.mutation<
      CreateConversationWrapupMessageApiResponse,
      CreateConversationWrapupMessageApiArg
    >({
      query: ({ csid, text: message }) => ({
        version: 1,
        path: `conversations/${csid}/wrapup`,
        method: 'POST',
        body: { message },
      }),
    }),
    updateConversationBlindAssignmentTransfer: builder.mutation<
      UpdateConversationBlindAssignmentTransferApiResponse,
      UpdateConversationBlindAssignmentTransferApiArg
    >({
      query: (arg) => ({
        version: 1,
        path: `conversations/${arg.csid}/transfer/blind`,
        method: 'PUT',
        body: {
          to: arg.to,
          ...(arg.entity === 'text' && arg.text && { message: arg.text }),
          ...(arg.entity === 'number' && { entity: arg.entity }),
        },
      }),
    }),
    updateConversationBlindAssignmentTransferAnswer: builder.mutation<
      UpdateConversationBlindAssignmentTransferAnswerApiResponse,
      UpdateConversationBlindAssignmentTransferAnswerApiArg
    >({
      query: ({ csid, didAccept: did_accept }) => ({
        version: 1,
        path: `conversations/${csid}/transfer/answer`,
        method: 'PUT',
        body: { did_accept },
      }),
    }),
    updateConversationAssistedAssignmentTransfer: builder.mutation<
      UpdateConversationAssistedAssignmentTransferApiResponse,
      UpdateConversationAssistedAssignmentTransferApiArg
    >({
      query: ({ csid, userId }) => ({
        version: 1,
        path: `conversations/${csid}/transfer/assisted`,
        method: 'PUT',
        body: { to: userId },
      }),
    }),
    updateConversationAssistedAssignmentTransferAnswer: builder.mutation<
      UpdateConversationAssistedAssignmentTransferAnswerApiResponse,
      UpdateConversationAssistedAssignmentTransferAnswerApiArg
    >({
      query: ({ csid, didAccept: accepted }) => ({
        version: 1,
        path: `conversations/${csid}/transfer/assisted/resolve`,
        method: 'PUT',
        body: { accepted },
      }),
    }),
    updateConversationQueueTransfer: builder.mutation<
      UpdateConversationQueueTransferApiResponse,
      UpdateConversationQueueTransferApiArg
    >({
      query: ({ csid, queueId: queue_id }) => ({
        version: 1,
        path: `conversations/${csid}/transfer/queue`,
        method: 'PUT',
        body: { queue_id },
      }),
    }),
    updateConversationPendingStatus: builder.mutation<
      UpdateConversationPendingStatusApiResponse,
      UpdateConversationPendingStatusApiArg
    >({
      query: ({ csid, timestamp, queueId: queue_id }) => ({
        version: 1,
        method: 'PUT',
        path: `conversations/${csid}/pending`,
        body: { timestamp, ...(queue_id && { queue_id }) },
      }),
    }),
    updateConversationCancelPending: builder.mutation<
      UpdateConversationCancelPendingApiResponse,
      UpdateConversationCancelPendingApiArg
    >({
      query: ({ csid }) => ({
        version: 1,
        method: 'PUT',
        path: `conversations/${csid}/cancel_pending`,
      }),
    }),
    createConversationsCsv: builder.mutation<
      ApiResponse,
      CreateConversationsCsvApiArg
    >({
      query: ({
        email: to_email,
        dateRange: [from_datetime, to_datetime],
      }) => ({
        version: 1,
        path: 'conversations/exports',
        method: 'POST',
        body: { to_email, from_datetime, to_datetime },
      }),
    }),
    readScheduledConversationBatches: builder.query<
      ReadScheduledConversationBatchesApiResponse,
      void
    >({
      query: () => ({
        version: 1,
        path: 'conversations/scheduled/batches',
      }),
      transformResponse: transformReadScheduledConversationBatchesResponse,
    }),
    createScheduledConversationBatch: builder.query<
      CreateScheduledConversationBatchApiResponse,
      CreateScheduledConversationBatchApiArg
    >({
      query: ({
        name,
        date,
        outboundNumber: outbound_number,
        queueId: queue_id,
        csvPath: csv_url,
      }) => ({
        version: 1,
        path: 'conversations/scheduled',
        method: 'POST',
        body: {
          channel: 'callback',
          outbound_number,
          name,
          date,
          queue_id,
          csv_url,
        },
      }),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          const {
            data: { data: batch },
          } = await queryFulfilled;

          dispatch(
            conversationsApi.util.updateQueryData(
              'readScheduledConversationBatches',
              undefined,
              (draft) => {
                const mutableBatches = draft.data.batches as DeepWritable<
                  typeof draft.data.batches
                >;

                mutableBatches.unshift(batch);
              },
            ),
          );
        } catch {
          // do nothing
        }
      },
    }),
    updateConversationCallRecordingPlayback: builder.mutation<
      UpdateConversationCallRecordingPlaybackApiResponse,
      UpdateConversationCallRecordingPlaybackApiArg
    >({
      query: ({ csid, state }) => ({
        version: 1,
        path: `conversations/${csid}/recordings/${state}`,
      }),
    }),
    updateConversationMuteCall: builder.mutation<
      ResultApiResponse,
      UpdateConversationMuteCallApiArg
    >({
      query: ({ csid, mute }) => ({
        version: 1,
        path: `conversations/${csid}/mute`,
        body: { mute },
      }),
    }),
    updateConversationHoldCall: builder.mutation<
      ResultApiResponse,
      UpdateConversationHoldCallApiArg
    >({
      query: ({ csid, userId, hold }) => ({
        version: 1,
        path: `conversations/${csid}/hold`,
        body: { hold, userId },
      }),
    }),
    updateConversationCaller: builder.mutation<
      ResultApiResponse,
      UpdateConversationCallerApiArg
    >({
      query: ({ csid, userId, action }) => ({
        version: 2,
        path: `conversations/${csid}/${action}`,
        method: 'PUT',
        body: { userId },
      }),
    }),
    readConversationsValidationBulk: builder.query<
      ReadConversationsValidationBulkApiResponse,
      ReadConversationsValidationBulkApiArg
    >({
      query: ({ csids }) => ({
        version: 3,
        method: 'POST', // This uses a POST as there can potentially be lots of csids.
        path: 'conversations/bulk/validation',
        body: { csids },
      }),
    }),
    createConversationMessageBulk: builder.mutation<
      ApiResponse,
      CreateConversationMessageBulkApiArg
    >({
      query: ({ csids, plainText: plain_text, htmlText: html_text }) => ({
        version: 1,
        path: 'conversations/bulk/reply',
        method: 'POST',
        body: { csids, plain_text, html_text },
      }),
    }),
    createConversationWrapupMessageBulk: builder.mutation<
      ApiResponse,
      CreateConversationWrapupMessageBulkApiArg
    >({
      query: ({ csids, text: message }) => ({
        version: 1,
        path: 'conversations/bulk/wrapup',
        method: 'POST',
        body: { csids, message },
      }),
    }),
    updateConversationTagBulk: builder.mutation<
      ApiResponse,
      UpdateConversationTagBulkApiArg
    >({
      query: ({ csids, names: tag_names, action }) => ({
        version: 1,
        path: 'conversations/bulk/tag',
        method: action === 'add_tag' ? 'POST' : 'DELETE',
        body: { csids, tag_names },
      }),
    }),
    updateConversationCloseBulk: builder.mutation<
      ApiResponse,
      UpdateConversationCloseBulkApiArg
    >({
      query: ({ csids }) => ({
        version: 1,
        path: 'conversations/bulk/close',
        method: 'POST',
        body: { csids },
      }),
    }),
    updateConversationCancelCsatBulk: builder.mutation<
      ApiResponse,
      UpdateConversationCancelCsatApiArg
    >({
      query: ({ csids }) => ({
        version: 1,
        path: 'conversations/bulk/cancel_csat',
        method: 'POST',
        body: { csids },
      }),
    }),
    updateConversationLinkBulk: builder.mutation<
      ApiResponse,
      UpdateConversationLinkBulkApiArg
    >({
      query: ({ csids, link_type, parent_csid }) => ({
        version: 1,
        path: 'conversations/bulk/link',
        method: 'POST',
        body: { csids, link_type, parent_csid },
      }),
    }),
    updateConversationQueueTransferBulk: builder.mutation<
      ApiResponse,
      UpdateConversationQueueTransferBulkApiArg
    >({
      query: ({ csids, queueId: new_queue_id }) => ({
        version: 1,
        path: 'conversations/bulk/transfer/queue',
        method: 'POST',
        body: { csids, new_queue_id },
      }),
    }),
    readConversationBulkAction: builder.query<
      ReadConversationBulkActionApiResponse,
      ReadConversationBulkActionApiArg
    >({
      query: ({ bulkId }) => ({
        version: 1,
        path: `conversations/bulk/${bulkId}`,
      }),
      transformResponse: transformReadConversationBulkAction,
    }),
    readConversationBulkActionFailedActions: builder.query<
      ReadConversationBulkActionFailedActionsApiResponse,
      ReadConversationBulkActionFailedActionsApiArg
    >({
      query: ({ bulkId }) => ({
        version: 1,
        path: `conversations/bulk/${bulkId}/failed_actions`,
      }),
      transformResponse: transformReadConversationBulkActionFailedActions,
    }),
    readConversationMessageDeliveryEvents: builder.query<
      ReadConversationMessageDeliveryEventsResponse,
      ReadConversationMessageDeliveryEventsArg
    >({
      query: ({ csid, messageId, filter }) => ({
        version: 4,
        path: `conversations/${csid}/messages/${messageId}/delivery-events`,
        params: {
          filter,
        },
      }),
      transformResponse: transformReadConversationMessageDeliveryEvents,
    }),
    readOrGenerateSummary: builder.query<
      ReadOrGenerateSummaryApiResponse,
      ReadOrGenerateSummaryApiArg
    >({
      query: ({ csid, regenerate, view, language }) => ({
        version: 4,
        method: 'POST',
        path: `conversations/${csid}/summary`,
        params: { regenerate },
        body: { view, language },
        timeout: 20000,
      }),
    }),
    readConversationCustomCard: builder.query<
      ReadConversationCustomCardApiResponse,
      ReadConversationCustomCardApiArg
    >({
      query: ({ csid, hookId }) => ({
        version: 3,
        path: `conversations/${csid}/custom_card/${hookId}`,
      }),
    }),
    readConversationAutomationActivities: builder.query<
      ReadConversationAutomationActivitiesApiResponse,
      ReadConversationAutomationActivitiesApiArg
    >({
      query: ({ csid, automationRunId }) => ({
        version: 1,
        path: `conversations/${csid}/automation_activities/${automationRunId}`,
      }),
    }),
    redactMessage: builder.mutation<
      CreateConversationMessageApiResponse,
      RedactConversationMessageApiArg
    >({
      query: ({ csid, messageId, attributeOverrides }) => ({
        version: 3,
        path: `conversations/${csid}/messages/${messageId}/redact`,
        method: 'POST',
        body: { attribute_overrides: attributeOverrides },
      }),
    }),
    suggestConversationTags: builder.query<
      SuggestConversationTagsApiResponse,
      SuggestConversationTagsApiArg
    >({
      query: ({ csid }) => ({
        version: 4,
        path: `conversations/${csid}/tag-suggestions`,
      }),
    }),
  }),
});
